import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MessageSquare, CheckCircle2, Calendar, Target } from "lucide-react";
import GlowEffect from "../common/GlowEffect";
import { useAIAgentData } from "../../hooks/useAIAgentData";

// Icon mapping dictionary to resolve string names to Lucide icon components
const iconMap: Record<string, React.ComponentType<any>> = {
  MessageSquare,
  CheckCircle2,
  Calendar,
  Target,
};

function renderIcon(name: string, size: number, className?: string) {
  const Icon = iconMap[name];
  return Icon ? <Icon size={size} className={className} /> : null;
}

// Shared animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.12 },
  }),
} as const;

// Missed Visitor Animation (problem section) helper
function MissedVisitorAnimation() {
  const [count, setCount] = useState(0);
  const { data } = useAIAgentData();
  const { title, badge, statLabel, visitorCounts, funnelSteps, visitors, leftCounts } = data.missedVisitorAnimation;

  useEffect(() => {
    const interval = setInterval(() => {
      setCount(c => (c >= 4 ? 0 : c + 1));
    }, 1800);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full max-w-[380px] mx-auto">
      <div className="absolute -inset-6 bg-gradient-to-br from-[#A6A0F6]/15 to-[#1179F9]/10 rounded-3xl blur-2xl" />
      <div className="relative bg-[#0d0d2b]/90 backdrop-blur-md border border-white/10 rounded-2xl p-6 space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-white font-semibold">{title}</p>
          <span className="text-xs text-[#A6A0F6] bg-[#A6A0F6]/10 px-2 py-1 rounded-full">{badge}</span>
        </div>

        {/* Big stat */}
        <div className="text-center space-y-1">
          <motion.p
            key={count}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-6xl font-bold text-white"
          >
            {visitorCounts[count]}
          </motion.p>
          <p className="text-[#d9d9d9] text-sm">{statLabel}</p>
        </div>

        {/* Funnel */}
        <div className="space-y-3">
          {funnelSteps.map((row, i) => (
            <div key={i} className="space-y-1">
              <div className="flex justify-between text-xs text-[#d9d9d9]">
                <span>{row.label}</span>
                <span className="text-white font-medium">{Math.round(visitorCounts[count] * row.factor)}</span>
              </div>
              <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: row.color }}
                  initial={{ width: 0 }}
                  animate={{ width: `${row.pct}%` }}
                  transition={{ duration: 1, delay: i * 0.15 }}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Visitors leaving indicator */}
        <div className="flex items-center gap-3 pt-1 border-t border-white/10">
          <div className="flex -space-x-2">
            {visitors.slice(0, count + 1).map((v, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: v.delay }}
                className="w-7 h-7 rounded-full border-2 border-[#0d0d2b] flex items-center justify-center text-white text-xs font-bold"
                style={{ background: v.color }}
              >
                {["A", "B", "C", "D"][i]}
              </motion.div>
            ))}
          </div>
          <p className="text-[#d9d9d9] text-xs">
            <span className="text-red-400 font-semibold">{leftCounts[count]} visitors</span> left without connecting
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ProblemSection() {
  const { data } = useAIAgentData();
  const { title, subtitle, losses, footerText } = data.problemSection;

  return (
    <section className="relative px-5 sm:px-10 md:px-16 sm:py-16 py-8">
      <GlowEffect direction="left" />
      <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        {/* Left — Missed Visitor Animation */}
        <motion.div
          variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="flex justify-center items-center order-last lg:order-first"
        >
          <MissedVisitorAnimation />
        </motion.div>

        {/* Right — content */}
        <div className="space-y-8">
          <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="space-y-4">
            <h2 className="text-4xl md:text-6xl font-bold text-white leading-tight">
              {title}
            </h2>
            <p className="text-2xl text-[#d9d9d9] leading-relaxed">
              {subtitle}
            </p>
          </motion.div>

          <div className="space-y-5">
            {losses.map((item, i) => (
              <motion.div
                key={i}
                variants={fadeUp} initial="hidden" whileInView="visible"
                viewport={{ once: true }} custom={i + 1}
                className="flex items-center gap-4"
              >
                <div className="w-12 h-12 rounded-xl bg-[#1179F9]/15 flex items-center justify-center text-[#1179F9] flex-shrink-0">
                  {renderIcon(item.iconName, 22)}
                </div>
                <p className="text-2xl text-[#d9d9d9]">{item.label}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={5}
            className="pt-4"
          >
            <p className="text-2xl text-[#d9d9d9] leading-relaxed border-l-2 border-[#A6A0F6] pl-4">
              {footerText.includes("passive") ? (
                <>
                  Most websites remain <span className="text-white font-semibold">passive</span> — valuable opportunities are lost before the conversation even starts.
                </>
              ) : footerText}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
